import { Component, EventEmitter, Output, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { PoSelectOption, PoButtonModule, PoContainerModule, PoFieldModule, PoWidgetModule, PoModalModule, PoModalComponent, PoModalAction } from '@po-ui/ng-components';
import { ScannerComponent } from '../../../shared/components/scanner/scanner.component';
import { AudioService } from '../../../shared/service/audio.service';
import { ToasterComponent } from '../../../shared/components/toaster/toaster.component';
import { ToasterService } from '../../../shared/service/toaster.service';
import { TokenService } from '../../../shared/service/token.service';
import { GeneralCountService } from '../../../shared/service/general-count.service';

interface LooseCount {
  countCode: number;
  description: string;
  status: number;
  conferenceStatus: string;
  createdDate: string;
  itemsCount: number;
}

@Component({
  selector: 'app-loose-header',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    PoButtonModule,
    PoContainerModule,
    PoFieldModule,
    PoWidgetModule,
    PoModalModule,
    ScannerComponent,
    ToasterComponent
  ],
  templateUrl: './loose-header.component.html',
  styleUrl: './loose-header.component.css'
})
export class LooseHeaderComponent implements OnInit {
  @Output() isLoading = new EventEmitter<boolean>();
  @Output() onTerminalModalOpen = new EventEmitter();
  @ViewChild('modalNewCount') modalNewCount!: PoModalComponent;

  countCode: string = '';
  branchCode: number = 0;
  newCountDescription: string = '';
  statusFilter: string = 'all';
  statusFilterOptions: Array<PoSelectOption> = [
    { label: 'Todos', value: 'all' },
    { label: 'Em andamento', value: 'InProgress' },
    { label: 'Respondida', value: 'Answered' },
    { label: 'Finalizada', value: 'Finished' },
    { label: 'Cancelada', value: 'Canceled' },
    { label: 'Agrupada', value: 'Grouped' }
  ];

  countList: LooseCount[] = [];
  displayCountList: LooseCount[] = [];

  currentPage: number = 1;
  pageSize: number = 20;
  totalPages: number = 1;
  totalItems: number = 0;
  hasNextPage: boolean = false;
  isLoadingMore: boolean = false;

  modalPrimaryAction: PoModalAction = {
    action: () => this.confirmCreateCount(),
    label: 'Criar'
  };

  modalSecondaryAction: PoModalAction = {
    action: () => this.modalNewCount.close(),
    label: 'Cancelar'
  };

  constructor(
    private router: Router,
    private toasterService: ToasterService,
    private audioService: AudioService,
    private tokenService: TokenService,
    private generalCountService: GeneralCountService
  ) {}

  ngOnInit() {
    this.branchCode = Number(this.tokenService.getBranch()) || 0;
    this.loadCountList();
  }

  onScannedCode(barcode: string) {
    this.countCode = String(barcode ?? '');
    this.searchCount();
  }

  searchCount() {
    if (!this.countCode) return;

    const count = this.countList.find(c => c.countCode.toString() === this.countCode);
    
    if (count) {
      this.navigateToConference(count);
      return;
    }

    this.isLoading.emit(true);
    this.generalCountService.getGeneralCounts(this.branchCode, undefined, 1, 1000).subscribe({
      next: (response: any) => {
        if (response?.items) {
          const foundCount = response.items.find((item: any) => 
            (item.countCode || item.code).toString() === this.countCode
          );
          
          if (foundCount) {
            const status = foundCount.countStatus || foundCount.status;
            const countData: LooseCount = {
              countCode: foundCount.countCode || foundCount.code,
              description: foundCount.countDescription || foundCount.description || 'Sem descrição',
              status: status,
              conferenceStatus: this.getStatusText(status),
              createdDate: foundCount.lastChangeDate ? new Date(foundCount.lastChangeDate).toLocaleDateString('pt-BR') : '-',
              itemsCount: foundCount.itemsCount || 0
            };
            this.isLoading.emit(false);
            this.navigateToConference(countData);
          } else {
            this.validateAndCreateCount(this.countCode);
          }
        } else {
          this.validateAndCreateCount(this.countCode);
        }
      },
      error: () => {
        this.isLoading.emit(false);
        this.audioService.playBeep(5);
        this.toasterService.triggerAlert('Erro ao buscar contagem', 'error');
      }
    });
  }

  private validateAndCreateCount(barcode: string) {
    this.isLoading.emit(true);

    this.generalCountService.getProduct(barcode, this.branchCode).subscribe({
      next: (productResponse: any) => {
        if (productResponse?.items && productResponse.items.length > 0) {
          const product = productResponse.items[0];
          const description = `Contagem Avulsa - ${product.productCode}`;
          this.createNewCountWithProduct(description, barcode);
        } else {
          this.audioService.playBeep(5);
          this.toasterService.triggerAlert('Contagem não encontrada', 'error');
          this.isLoading.emit(false);
        }
      },
      error: () => {
        this.audioService.playBeep(5);
        this.toasterService.triggerAlert('Contagem não encontrada', 'error');
        this.isLoading.emit(false);
      }
    });
  }

  private createNewCountWithProduct(description: string, barcode: string) {
    this.generalCountService.postGeneralCount(this.branchCode, description).subscribe({
      next: (response: any) => {
        this.toasterService.triggerAlert('Contagem criada com sucesso', 'success');
        this.loadCountList();
        
        if (response?.code) {
          this.router.navigate(['/avulsa'], {
            queryParams: {
              branchCode: this.branchCode,
              countCode: response.code,
              description: description
            }
          });
        }
      },
      error: (error) => {
        this.toasterService.triggerAlert(
          error?.error?.[0]?.message || 'Erro ao criar contagem',
          'error'
        );
        this.isLoading.emit(false);
      }
    });
  }

  createNewCount() {
    const timestamp = new Date().toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric'
    });
    this.newCountDescription = `Contagem Avulsa - ${timestamp}`;
    this.modalNewCount.open();
  }

  confirmCreateCount() {
    if (!this.newCountDescription.trim()) {
      this.toasterService.triggerAlert('A descrição não pode estar vazia', 'error');
      return;
    }
    
    const createdDate = new Date().toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric'
    });
    
    this.modalNewCount.close();
    this.isLoading.emit(true);
    this.generalCountService.postGeneralCount(this.branchCode, this.newCountDescription).subscribe({
      next: (response: any) => {
        this.isLoading.emit(false);
        this.toasterService.triggerAlert('Contagem criada com sucesso', 'success');
        
        if (response?.code) {
          this.router.navigate(['/avulsa'], {
            queryParams: {
              branchCode: this.branchCode,
              countCode: response.code,
              description: this.newCountDescription,
              createdDate: createdDate
            }
          });
        }
      },
      error: (error) => {
        this.toasterService.triggerAlert(
          error?.error?.[0]?.message || 'Erro ao criar contagem',
          'error'
        );
        this.isLoading.emit(false);
      }
    });
  }

  onCountClick(count: LooseCount) {
    this.navigateToConference(count);
  }

  private navigateToConference(count: LooseCount) {
    this.router.navigate(['/avulsa'], {
      queryParams: {
        branchCode: this.branchCode,
        countCode: count.countCode,
        description: count.description,
        status: count.status,
        conferenceStatus: count.conferenceStatus,
        createdDate: count.createdDate,
        itemsCount: count.itemsCount
      }
    });
  }

  onStatusFilterChange(value: string) {
    this.statusFilter = value;
    this.currentPage = 1;
    this.countList = [];
    this.displayCountList = [];
    this.loadCountList();
  }

  private loadCountList() {
    if (!this.branchCode) return;

    if (this.currentPage === 1) {
      this.isLoading.emit(true);
    }
    
    const statusParam = this.statusFilter !== 'all' ? this.statusFilter : undefined;

    this.generalCountService.getGeneralCounts(this.branchCode, statusParam, this.currentPage, this.pageSize).subscribe({
      next: (response: any) => {
        if (response?.items) {
          const newItems = response.items.map((item: any) => {
            const status = item.countStatus || item.status;
            return {
              countCode: item.countCode || item.code,
              description: item.countDescription || item.description || 'Sem descrição',
              status: status,
              conferenceStatus: this.getStatusText(status),
              createdDate: item.lastChangeDate ? new Date(item.lastChangeDate).toLocaleDateString('pt-BR') : '-',
              itemsCount: item.itemsCount || 0
            };
          });
          
          if (this.currentPage === 1) {
            this.countList = newItems;
            this.displayCountList = [...newItems];
            this.totalPages = response.totalPages || 1;
            this.totalItems = response.totalItems || response.count || 0;
          } else {
            this.countList = [...this.countList, ...newItems];
            this.displayCountList = [...this.displayCountList, ...newItems];
          }
          
          this.hasNextPage = response.hasNext || false;
        }
        this.isLoading.emit(false);
        this.isLoadingMore = false;
      },
      error: (error) => {
        this.toasterService.triggerAlert('Erro ao carregar contagens', 'error');
        this.isLoading.emit(false);
        this.isLoadingMore = false;
      }
    });
  }

  private getStatusText(status: string | number): string {
    const statusStr = String(status);
    switch(statusStr) {
      case 'InProgress':
      case '1': 
        return 'in-progress';
      case 'Answered':
        return 'answered';
      case 'Finished':
      case '2':
        return 'closed';
      case 'Canceled':
        return 'canceled';
      case 'Grouped':
        return 'grouped';
      default: 
        return 'in-progress';
    }
  }

  getConferenceStatusInfo(status: string) {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'closed' || normalized === 'encerrada' || normalized === 'completed' || normalized === 'finished') {
      return { text: 'Finalizada', icon: 'po-icon-ok', class: 'conference-status-badge conference-status-closed' };
    }
    if (normalized === 'answered' || normalized === 'respondida') {
      return { text: 'Respondida', icon: 'po-icon-ok', class: 'conference-status-badge conference-status-answered' };
    }
    if (normalized === 'canceled' || normalized === 'cancelada') {
      return { text: 'Cancelada', icon: 'po-icon-close', class: 'conference-status-badge conference-status-canceled' };
    }
    if (normalized === 'grouped' || normalized === 'agrupada') {
      return { text: 'Agrupada', icon: 'po-icon-doc', class: 'conference-status-badge conference-status-grouped' };
    }
    return { text: 'Em andamento', icon: 'po-icon-refresh', class: 'conference-status-badge conference-status-in-progress' };
  }

  onScroll(event: any): void {
    const element = event.target;
    const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
    
    if (atBottom && this.hasNextPage && !this.isLoadingMore) {
      this.loadMoreCounts();
    }
  }

  loadMoreCounts(): void {
    if (this.isLoadingMore || !this.hasNextPage) return;
    
    this.isLoadingMore = true;
    this.currentPage++;
    this.loadCountList();
  }
}
